import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import classes from './styles.module.scss';

/**
 * Self-contained trim handle component
 * Handles all drag logic and emits pixel deltas to parent
 */

const getCssZoom = () => {
  const zoomValue = getComputedStyle(document.documentElement)
    .getPropertyValue('--zoom')
    .trim();
  return parseFloat(zoomValue) || 1;
};

export default function TrimHandle({
  side,
  onTrimStart,
  onTrimMove,
  onTrimEnd,
  disabled = false,
}) {
  const [isDragging, setIsDragging] = useState(false);
  const startXRef = useRef(0);
  const lastXRef = useRef(0);

  useEffect(() => {
    if (!isDragging) {
      return undefined;
    }

    const handleMouseMove = (e) => {
      e.preventDefault();
      const deltaX = e.clientX - lastXRef.current;
      lastXRef.current = e.clientX;

      const zoom = getCssZoom();
      const normalizedDelta = zoom !== 0 ? deltaX / zoom : deltaX;

      // Emit delta (negative for left handle, positive for right)
      const adjustedDelta = side === 'left' ? normalizedDelta : -normalizedDelta;
      onTrimMove(adjustedDelta);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      if (onTrimEnd) {
        onTrimEnd();
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, onTrimMove, onTrimEnd, side]);

  const handleMouseDown = (e) => {
    if (disabled) return;

    e.stopPropagation(); // Don't trigger timeline item selection
    e.preventDefault();

    startXRef.current = e.clientX;
    lastXRef.current = e.clientX;
    setIsDragging(true);
    if (onTrimStart) {
      onTrimStart();
    }
  };

  return (
    <div
      className={classNames(classes.TrimHandle, {
        [classes.left]: side === 'left',
        [classes.right]: side === 'right',
        [classes.dragging]: isDragging,
      })}
      onMouseDown={handleMouseDown}
      title={side === 'right' ? 'Trim Right Edge' : 'Trim Left Edge'}
    >
      {/* Bracket design - inspired by video editing apps */}
      <div className={classes.bracketContainer}>
        {/* Vertical line */}
        <div
          className={classNames(classes.verticalLine, {
            [classes.left]: side === 'left',
            [classes.right]: side === 'right',
            [classes.dragging]: isDragging,
          })}
        />

        {/* Top bracket */}
        <div
          className={classNames(classes.topBracket, {
            [classes.left]: side === 'left',
            [classes.right]: side === 'right',
            [classes.dragging]: isDragging,
          })}
        />

        {/* Bottom bracket */}
        <div
          className={classNames(classes.bottomBracket, {
            [classes.left]: side === 'left',
            [classes.right]: side === 'right',
            [classes.dragging]: isDragging,
          })}
        />

        {/* Center grip indicator (3 dots) */}
        <div
          className={classNames(classes.gripIndicator, {
            [classes.left]: side === 'left',
            [classes.right]: side === 'right',
            [classes.visible]: isDragging,
          })}
        >
          <div className={classes.gripDot} />
          <div className={classes.gripDot} />
          <div className={classes.gripDot} />
        </div>
      </div>

      {/* Drag area (wider for easier grabbing) */}
      <div className={classes.dragArea} />
    </div>
  );
}

